import { useState, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TransitionGroup } from 'react-transition-group';
import { Fade, Collapse, Typography, makeStyles } from '@material-ui/core';
import TextBoxWithTooltip from '../../../textBoxWithTooltip';
import ActionButton from '../../../actionButton';
import Tooltip from '../../../tooltip';
import ConditionalTooltip from '../../../conditionalTooltip';
import CustomCheckbox from '../../../customCheckbox';
import { ReactComponent as PartiallyCheckedIcon } from '../../../../../assets/icons/indeterminate_check_box.svg';
import { ReactComponent as InfoIcon } from '../../../../../assets/icons/info_light.svg';
import {
  PERMISSION_ACTIONS,
  PERMISSION_CODES,
  PERMISSIONS_WITH_GLOBAL_SCOPE,
  DISABLED_PERMISSION_CODES,
  PARTIALLY_DISABLED_PERMISSION_CODES,
  REVIEW_GROUP,
  REVIEW_ROOT_VIEW_PERMISSION_CODES,
  REVIEW_ROOT_ALL_POSSIBLE_PERMISSION_CODES,
  REVIEW_ROOT_CREATED_POSSIBLE_PERMISSION_CODES,
} from '../../../../../constants/permissions';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';
import {
  isArrayEmpty,
  getItemById,
  isValueInList,
} from '../../../../../utility/helpers';
import { preparePermissionScopeValues } from '../../../../../utility/roles';
import { getNestedViewPairsForPermission } from '../../../../../utility/permissions';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    borderBottom: `1px solid ${primary.bluish7}`,
    width: '100%',
  },
  groupPlaceholder: {
    cursor: 'pointer',
    padding: spacing(6, 0),
    transition: 'background-color .3s ease',
    height: 20,
  },
  permissionGroupWrapper: {
    cursor: 'pointer',
    background: primary.white,
    display: 'flex',
    transition: 'background-color .3s ease',
    '&:hover': {
      backgroundColor: primary.bluish9,
      '& $permissionName': {
        backgroundColor: primary.bluish9,
      },
    },
  },
  permissionName: {
    background: primary.white,
    cursor: 'pointer',
    position: 'sticky',
    left: 0,
    flexShrink: 0,
    padding: spacing(6, 0),
    width: 300,
    userSelect: 'none',
    transition: 'background-color .3s ease',
    zIndex: 19,
  },
  permissionContentColumn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexGrow: 1,
  },
  arrowButton: {
    position: 'sticky',
    right: 6,
    zIndex: 19,
  },
  expandIcon: {
    transform: 'rotate(0deg)',
    transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
  },
  expandIconOpened: {
    transform: 'rotate(180deg)',
  },
  contentWrapper: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 4,
    marginTop: spacing(1),
  },
  permissionsWrapper: {
    backgroundColor: primary.white,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: 4,
    position: 'sticky',
    left: 0,
    paddingBottom: spacing(6),
    width: 300,
    zIndex: 19,
  },
  permissionWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    userSelect: 'none',
    height: 58,
  },
  permissionLabel: {
    userSelect: 'none',
  },
  permissionInfoButton: {
    flexShrink: 0,
  },
  infoButtonIcon: {
    width: 20,
    height: 20,
  },
  rolePermissionsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 4,
  },
  rolePermissions: {
    display: 'flex',
    flexWrap: 'nowrap',
    columnGap: 4,
    height: 58,
  },
  roleColumn: {
    boxSizing: 'border-box',
    backgroundColor: primary.bluish9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: 90,
  },
  additionalRoleColumn: {
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish7}`,
  },
  expandableScopeColumn: {
    position: 'relative',
    display: 'flex',
    gap: 12,
    width: 180,
  },
  checkboxWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  disabledCheckbox: {
    cursor: 'default',
  },
  scopeLabel: {
    marginBottom: 0,
  },
  expandButtonWrapper: {
    position: 'absolute',
    top: 26,
    right: 12,
  },
  expandButton: {
    width: 24,
    height: 24,
  },
  expandButtonIcon: {
    width: 14,
    height: 14,
    transition: 'transform .3s cubic-bezier(0.77, 0.2, 0.05, 1)',
  },
  expandButtonIconOpened: {
    transform: 'rotate(90deg)',
  },
  expandButtonIconDisabled: {
    '& path': {
      fill: primary.bluish7,
    },
  },
}));

const Row = ({
  translations,
  roles,
  permissionGroup,
  permissionsByRole,
  isExpanded,
  onToggleExpand,
  onTogglePermission,
}) => {
  const classes = useStyles();

  const [expandedPermissionScopes, setExpandedPermissionScopes] = useState([]);

  const onTogglePermissionGroup = useCallback(
    e => {
      e.stopPropagation();

      onToggleExpand(isExpanded ? '' : permissionGroup.group);
    },
    [permissionGroup.group, isExpanded, onToggleExpand]
  );

  const onTogglePermissionScopes = useCallback(
    permissionCode => () => {
      setExpandedPermissionScopes(prevExpandedPermissionScopes =>
        isValueInList(prevExpandedPermissionScopes, permissionCode)
          ? prevExpandedPermissionScopes.filter(code => code !== permissionCode)
          : [...prevExpandedPermissionScopes, permissionCode]
      );
    },
    []
  );

  const preparePermissionScopeLabel = useCallback(
    (
      isGlobalScopePermission,
      role,
      permissionCode = '',
      scopeLabel = '',
      isScopeExpanded = false
    ) => {
      if (isGlobalScopePermission) {
        return translations.globalScope.label;
      }
      if (role.hasPermissionsWithMultipleScopes) {
        if (
          isScopeExpanded ||
          isValueInList(expandedPermissionScopes, permissionCode)
        ) {
          return scopeLabel;
        }
        return translations.expandableScope.label;
      }

      return role.permissionScopes[0]?.label;
    },
    [translations, expandedPermissionScopes]
  );

  const preparePermissionCheckboxTooltip = useCallback(
    (
      role,
      permission,
      isGlobalScopePermission,
      scope = null,
      isScopeExpanded = false
    ) => {
      const roleName = role.isAdditionalRole ? role.name : role.label;

      let scopeTooltip = '';
      if (isGlobalScopePermission) {
        scopeTooltip = translations.globalScope.tooltip;
      } else if (role.hasPermissionsWithMultipleScopes) {
        scopeTooltip =
          isScopeExpanded && scope
            ? scope.tooltip
            : translations.expandableScope.tooltip;
      } else {
        scopeTooltip = role.permissionScopes[0]?.tooltip || '';
      }

      const baseMessage = translations.permissionCheckboxTooltip
        .replace('[ROLE]', roleName)
        .replace('[SCOPE]', scopeTooltip);

      return permission.tooltip
        ? `${baseMessage} ${permission.tooltip}`
        : baseMessage;
    },
    [translations]
  );

  return (
    <div className={classes.root}>
      <div
        className={classes.permissionGroupWrapper}
        onClick={onTogglePermissionGroup}
      >
        <div className={classes.permissionName}>
          <TextBoxWithTooltip variant="h5" text={permissionGroup.label} />
        </div>
        <div className={classes.permissionContentColumn}>
          <ActionButton
            className={classes.arrowButton}
            type={ACTION_BUTTON_TYPES.ARROW}
            iconClass={classNames(classes.expandIcon, {
              [classes.expandIconOpened]: isExpanded,
            })}
          />
        </div>
      </div>
      <Collapse in={isExpanded} mountOnEnter unmountOnExit>
        <div className={classes.contentWrapper}>
          <div className={classes.permissionsWrapper}>
            {permissionGroup.permissions.map(permission => (
              <div
                key={`permission_${permission.code}`}
                className={classes.permissionWrapper}
              >
                <Typography
                  className={classes.permissionLabel}
                  variant="subtitle2"
                >
                  {permission.label}
                </Typography>
                {!!permission.tooltip && (
                  <Tooltip
                    customIconClass={classNames(
                      classes.permissionInfoButton,
                      classes.infoButtonIcon
                    )}
                    text={permission.tooltip}
                    icon={InfoIcon}
                  />
                )}
              </div>
            ))}
          </div>
          <div className={classes.rolePermissionsWrapper}>
            {permissionGroup.permissions.map(permission => (
              <TransitionGroup
                key={`permission_${permission.code}`}
                className={classes.rolePermissions}
              >
                {roles.map(role => {
                  const isGlobalScopePermission =
                    PERMISSIONS_WITH_GLOBAL_SCOPE.includes(permission.code);
                  const rolePermissions =
                    permissionsByRole?.[role.name]?.permissions;
                  const rolePermission = getItemById(
                    rolePermissions || [],
                    permission.code,
                    'code'
                  );
                  const isPartiallyChecked =
                    !isGlobalScopePermission &&
                    role.hasPermissionsWithMultipleScopes &&
                    rolePermission &&
                    !isArrayEmpty(rolePermission.scopes) &&
                    rolePermission.scopes.length < role.permissionScopes.length;
                  const isReviewGroup = permissionGroup.group === REVIEW_GROUP;
                  const nestedViewPairs = getNestedViewPairsForPermission(
                    permission.code
                  );
                  const isMissingNestedView = nestedViewPairs.some(
                    pair =>
                      !getItemById(rolePermissions || [], pair.view, 'code')
                  );

                  let isPermissionDisabled;
                  if (isReviewGroup) {
                    const isRootPermission =
                      REVIEW_ROOT_VIEW_PERMISSION_CODES.includes(
                        permission.code
                      );
                    const isRootViewAllActive = !!getItemById(
                      rolePermissions || [],
                      PERMISSION_CODES.SURVEYS_VIEW_ALL,
                      'code'
                    );
                    const isRootViewCreatedActive = !!getItemById(
                      rolePermissions || [],
                      PERMISSION_CODES.SURVEYS_VIEW_CREATED,
                      'code'
                    );
                    const isRootViewActive =
                      isRootViewAllActive || isRootViewCreatedActive;

                    const isAllPairPermission =
                      REVIEW_ROOT_ALL_POSSIBLE_PERMISSION_CODES.includes(
                        permission.code
                      );
                    const isCreatedPairPermission =
                      REVIEW_ROOT_CREATED_POSSIBLE_PERMISSION_CODES.includes(
                        permission.code
                      );
                    const isWrongPair =
                      (isAllPairPermission &&
                        isRootViewCreatedActive &&
                        !isRootViewAllActive) ||
                      (isCreatedPairPermission &&
                        isRootViewAllActive &&
                        !isRootViewCreatedActive);

                    if (isWrongPair) {
                      isPermissionDisabled = role.isDisabled || true;
                    } else if (isRootPermission) {
                      isPermissionDisabled = role.isDisabled;
                    } else if (!isRootViewActive) {
                      isPermissionDisabled = role.isDisabled || true;
                    } else if (permission.action === PERMISSION_ACTIONS.VIEW) {
                      isPermissionDisabled = role.isDisabled;
                    } else {
                      const prefix = permission.code.split('.')[0];
                      const viewPermissionCode = `${prefix}.${PERMISSION_ACTIONS.VIEW}`;
                      const groupHasPrefixView = getItemById(
                        permissionGroup.permissions,
                        viewPermissionCode,
                        'code'
                      );
                      isPermissionDisabled =
                        role.isDisabled ||
                        (!!groupHasPrefixView &&
                          !getItemById(
                            rolePermissions || [],
                            viewPermissionCode,
                            'code'
                          ));
                    }
                  } else {
                    isPermissionDisabled =
                      role.isDisabled ||
                      isValueInList(
                        DISABLED_PERMISSION_CODES,
                        permission.code
                      ) ||
                      (!role.hasPermissionsWithMultipleScopes &&
                        PARTIALLY_DISABLED_PERMISSION_CODES.some(
                          perm =>
                            perm.code === permission.code &&
                            perm.scopes.includes(role.permissionScopes[0].value)
                        )) ||
                      (permission.action !== PERMISSION_ACTIONS.VIEW &&
                        getItemById(
                          permissionGroup.permissions,
                          `${permissionGroup.group}.${PERMISSION_ACTIONS.VIEW}`,
                          'code'
                        ) &&
                        !getItemById(
                          rolePermissions || [],
                          `${permissionGroup.group}.${PERMISSION_ACTIONS.VIEW}`,
                          'code'
                        ));
                  }
                  isPermissionDisabled =
                    isPermissionDisabled || isMissingNestedView;
                  const groupViewPermissionCode = `${permissionGroup.group}.${PERMISSION_ACTIONS.VIEW}`;
                  const groupViewPermission =
                    !isReviewGroup &&
                    !isGlobalScopePermission &&
                    permission.action !== PERMISSION_ACTIONS.VIEW &&
                    role.hasPermissionsWithMultipleScopes
                      ? getItemById(
                          rolePermissions || [],
                          groupViewPermissionCode,
                          'code'
                        )
                      : null;
                  const isNestedViewPartiallyScoped =
                    !isGlobalScopePermission &&
                    role.hasPermissionsWithMultipleScopes &&
                    nestedViewPairs.some(pair => {
                      if (PERMISSIONS_WITH_GLOBAL_SCOPE.includes(pair.view)) {
                        return false;
                      }

                      const nestedViewPermission = getItemById(
                        rolePermissions || [],
                        pair.view,
                        'code'
                      );

                      return (
                        !!nestedViewPermission &&
                        !isArrayEmpty(nestedViewPermission.scopes) &&
                        nestedViewPermission.scopes.length <
                          role.permissionScopes.length
                      );
                    });
                  const isViewPartiallyScoped =
                    isNestedViewPartiallyScoped ||
                    (!!groupViewPermission &&
                      !PERMISSIONS_WITH_GLOBAL_SCOPE.includes(
                        groupViewPermissionCode
                      ) &&
                      !isArrayEmpty(groupViewPermission.scopes) &&
                      groupViewPermission.scopes.length <
                        role.permissionScopes.length);
                  const isScopeSettingsExpanded =
                    isViewPartiallyScoped ||
                    isValueInList(expandedPermissionScopes, permission.code);

                  return (
                    <Fade key={`role_${role.id}`} in unmountOnExit>
                      <div
                        className={classNames(classes.roleColumn, {
                          [classes.expandableScopeColumn]:
                            role.hasPermissionsWithMultipleScopes,
                          [classes.additionalRoleColumn]: role.isAdditionalRole,
                        })}
                      >
                        {!isGlobalScopePermission &&
                        role.hasPermissionsWithMultipleScopes &&
                        isScopeSettingsExpanded ? (
                          role.permissionScopes.map(scope => {
                            const groupHasViewPermission = getItemById(
                              permissionGroup.permissions,
                              `${permissionGroup.group}.${PERMISSION_ACTIONS.VIEW}`,
                              'code'
                            );
                            const viewPermission = getItemById(
                              rolePermissions,
                              `${permissionGroup.group}.${PERMISSION_ACTIONS.VIEW}`,
                              'code'
                            );
                            const isMissingNestedViewScope =
                              nestedViewPairs.some(pair => {
                                const nestedViewPermission = getItemById(
                                  rolePermissions || [],
                                  pair.view,
                                  'code'
                                );

                                return (
                                  !nestedViewPermission ||
                                  (!isValueInList(
                                    PERMISSIONS_WITH_GLOBAL_SCOPE,
                                    pair.view
                                  ) &&
                                    !nestedViewPermission.scopes.includes(
                                      scope.value
                                    ))
                                );
                              });
                            const isScopePermissionDisabled =
                              isValueInList(
                                DISABLED_PERMISSION_CODES,
                                permission.code
                              ) ||
                              PARTIALLY_DISABLED_PERMISSION_CODES.some(
                                perm =>
                                  perm.code === permission.code &&
                                  perm.scopes.includes(scope.value)
                              ) ||
                              isMissingNestedViewScope ||
                              (permission.action !== PERMISSION_ACTIONS.VIEW &&
                                groupHasViewPermission &&
                                (!viewPermission ||
                                  (!isValueInList(
                                    PERMISSIONS_WITH_GLOBAL_SCOPE,
                                    viewPermission?.code
                                  ) &&
                                    !viewPermission.scopes.includes(
                                      scope.value
                                    ))));

                            return (
                              <ConditionalTooltip
                                key={`moderator_setting_${scope.value}`}
                                message={preparePermissionCheckboxTooltip(
                                  role,
                                  permission,
                                  isGlobalScopePermission,
                                  scope,
                                  isScopeSettingsExpanded
                                )}
                                addTooltip
                                noDefaultStyles
                                className={classes.checkboxWrapper}
                              >
                                <CustomCheckbox
                                  customCheckBoxRoot={classNames({
                                    [classes.disabledCheckbox]:
                                      isScopePermissionDisabled,
                                  })}
                                  labelClass={classes.scopeLabel}
                                  label={preparePermissionScopeLabel(
                                    isGlobalScopePermission,
                                    role,
                                    permission.code,
                                    scope.label,
                                    isScopeSettingsExpanded
                                  )}
                                  isChecked={
                                    !!rolePermission &&
                                    rolePermission.scopes.includes(scope.value)
                                  }
                                  disabled={isScopePermissionDisabled}
                                  onChange={onTogglePermission(
                                    role,
                                    permission.code,
                                    rolePermission,
                                    [scope.value],
                                    permissionGroup.group,
                                    permission.action,
                                    isScopeSettingsExpanded
                                  )}
                                  isControlled
                                />
                              </ConditionalTooltip>
                            );
                          })
                        ) : (
                          <ConditionalTooltip
                            message={preparePermissionCheckboxTooltip(
                              role,
                              permission,
                              isGlobalScopePermission
                            )}
                            addTooltip
                            noDefaultStyles
                            className={classes.checkboxWrapper}
                          >
                            <CustomCheckbox
                              className={classes.checkboxWrapper}
                              customCheckBoxRoot={classNames({
                                [classes.disabledCheckbox]:
                                  isPermissionDisabled,
                              })}
                              labelClass={classes.scopeLabel}
                              label={preparePermissionScopeLabel(
                                isGlobalScopePermission,
                                role
                              )}
                              isChecked={!!rolePermission}
                              icon={
                                isPartiallyChecked ? PartiallyCheckedIcon : null
                              }
                              disabled={isPermissionDisabled}
                              onChange={onTogglePermission(
                                role,
                                permission.code,
                                rolePermission,
                                preparePermissionScopeValues(
                                  isGlobalScopePermission,
                                  role.permissionScopes
                                ),
                                permissionGroup.group,
                                permission.action
                              )}
                              isControlled
                            />
                          </ConditionalTooltip>
                        )}
                        {role.hasPermissionsWithMultipleScopes &&
                          !isGlobalScopePermission &&
                          !isPermissionDisabled && (
                            <ActionButton
                              className={classes.expandButtonWrapper}
                              iconButtonClass={classes.expandButton}
                              iconClass={classNames(classes.expandButtonIcon, {
                                [classes.expandButtonIconOpened]:
                                  isScopeSettingsExpanded,
                                [classes.expandButtonIconDisabled]:
                                  isViewPartiallyScoped,
                              })}
                              tooltipText={
                                isScopeSettingsExpanded
                                  ? translations.scopeSettingsTooltip.expanded
                                  : translations.scopeSettingsTooltip.default
                              }
                              type={ACTION_BUTTON_TYPES.EXPAND}
                              isDisabled={isViewPartiallyScoped}
                              disabledTooltipText={
                                translations.scopeSettingsTooltip.disabled
                              }
                              onClickHandler={onTogglePermissionScopes(
                                permission.code
                              )}
                            />
                          )}
                      </div>
                    </Fade>
                  );
                })}
              </TransitionGroup>
            ))}
          </div>
        </div>
      </Collapse>
    </div>
  );
};

Row.propTypes = {
  translations: PropTypes.object.isRequired,
  roles: PropTypes.arrayOf(PropTypes.object).isRequired,
  permissionGroup: PropTypes.object.isRequired,
  permissionsByRole: PropTypes.object.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onTogglePermission: PropTypes.func.isRequired,
};

export default memo(Row);
